/**
 * DeckStackView - Stack layout grouping cards by type, CMC, color identity
 *
 * Features:
 * - Cards displayed as stacked thumbnails (top portion visible)
 * - Groups split into multiple stacks when > 10 cards
 * - Group by: Type, Mana Cost, Color Identity
 * - Cards expose hover preview and click-to-open-details only (no inline actions)
 */
import type { ReactElement } from 'react';
import { useMemo } from 'react';

import clsx from 'clsx';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import type { DeckFormat } from '@/types/deck';

import useCardGroups from '../hooks/useCardGroups';
import { EnrichedDeckCard } from '../types';
import CardSelectOverlayButton from './CardSelectOverlayButton';
import CommanderSection from './CommanderSection';
import SectionTitle from './SectionTitle';

interface StackGroup {
  key: string;
  label: string;
  cards: EnrichedDeckCard[];
  totalQuantity: number;
  sortOrder: number;
}

/** Maximum cards per stack before splitting */
const MAX_CARDS_PER_STACK = 10;
const cardOffset = 36; // pixels between card tops (was 28)
const visibleHeight = 72; // height of visible portion for stacked cards
const cardWidth = 180; // w-45 equivalent
const fullCardHeight = Math.round(cardWidth * (680 / 488)); // MTG card aspect ratio ~223px

// ============================================================================
// Stack Card Component
// ============================================================================

interface StackCardProps {
  deckCard: EnrichedDeckCard;
  isLast: boolean;
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
}

function StackCard({ deckCard, isLast, onCardSelect }: StackCardProps): ReactElement {
  const card = deckCard.card;

  return (
    <CardHoverPreview imageUris={deckCard.display_image_uris} name={card.name} layout={card.layout} cardFaces={card.card_faces}>
      <div
        className={clsx('group/card relative overflow-hidden shadow-md shadow-black/40', isLast ? 'rounded-lg' : 'rounded-t-lg')}
        style={{ height: isLast ? `${fullCardHeight}px` : `${visibleHeight}px` }}
      >
        {/* Card image clipped to show only top portion */}
        <CardImage
          imageUris={deckCard.display_image_uris}
          name={card.name}
          size="normal"
          className="h-auto! w-full! rounded-lg object-cover object-top"
        />

        {/* Quantity Badge */}
        {deckCard.quantity > 1 && (
          <div className="pointer-events-none absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900/90 text-xs font-bold text-zinc-100 shadow">
            {deckCard.quantity}
          </div>
        )}

        {/* Click target to open details panel */}
        {onCardSelect && <CardSelectOverlayButton cardName={card.name} onSelect={() => onCardSelect(card.id, deckCard.id)} />}
      </div>
    </CardHoverPreview>
  );
}

// ============================================================================
// Card Stack Component
// ============================================================================

interface CardStackProps {
  group: StackGroup;
  stackIndex?: number;
  totalStacks?: number;
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
}

function CardStack({ group, stackIndex, totalStacks, onCardSelect }: CardStackProps): ReactElement {
  // Build the stack label
  const stackLabel = totalStacks && totalStacks > 1 ? `${group.label} (${(stackIndex ?? 0) + 1}/${totalStacks})` : group.label;
  const isLastCard = (index: number) => index === group.cards.length - 1;

  return (
    <div className="group/stack w-45">
      {/* Stack Header */}
      <SectionTitle title={stackLabel} uniqueCount={group.cards.length} totalCount={group.totalQuantity} />

      {/* Stacked Cards */}
      <div className="relative">
        {/* Cards in Stack - last card shows fully */}
        <div
          className="relative"
          style={{
            height: `${(group.cards.length - 1) * cardOffset + fullCardHeight}px`,
          }}
        >
          {group.cards.map((deckCard, index) => (
            <div
              key={deckCard.id}
              className={clsx('absolute left-0 transition-all duration-200')}
              style={{
                top: `${index * cardOffset}px`,
                width: `${cardWidth}px`,
                zIndex: index + 1, // Higher index = higher z-index (card on top)
              }}
            >
              {deckCard.card ? (
                <StackCard deckCard={deckCard} isLast={isLastCard(index)} onCardSelect={onCardSelect} />
              ) : (
                <div
                  className={clsx('w-full animate-pulse bg-zinc-800/50', isLastCard(index) ? 'rounded-lg' : 'rounded-t-lg')}
                  style={{ height: isLastCard(index) ? `${fullCardHeight}px` : `${visibleHeight}px` }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helper: Split groups into multiple stacks
// ============================================================================

interface SplitStack extends StackGroup {
  stackIndex: number;
  totalStacks: number;
}

function splitIntoStacks(groups: StackGroup[]): SplitStack[] {
  const result: SplitStack[] = [];

  for (const group of groups) {
    if (group.cards.length <= MAX_CARDS_PER_STACK) {
      // No need to split
      result.push({ ...group, stackIndex: 0, totalStacks: 1 });
    } else {
      // Split evenly: calculate how many stacks needed
      const totalStacks = Math.ceil(group.cards.length / MAX_CARDS_PER_STACK);
      // Distribute cards as evenly as possible
      const baseSize = Math.floor(group.cards.length / totalStacks);
      const remainder = group.cards.length % totalStacks;

      let cardIndex = 0;
      for (let stackIdx = 0; stackIdx < totalStacks; stackIdx++) {
        // First 'remainder' stacks get one extra card
        const stackSize = baseSize + (stackIdx < remainder ? 1 : 0);
        const stackCards = group.cards.slice(cardIndex, cardIndex + stackSize);
        cardIndex += stackSize;

        result.push({
          key: `${group.key}-${stackIdx}`,
          label: group.label,
          cards: stackCards,
          totalQuantity: stackCards.reduce((sum, c) => sum + c.quantity, 0),
          sortOrder: group.sortOrder + stackIdx * 0.01, // Keep stacks together
          stackIndex: stackIdx,
          totalStacks,
        });
      }
    }
  }

  return result;
}

// ============================================================================
// Main Component
// ============================================================================

export interface DeckStackViewProps {
  deckId: string;
  cards: EnrichedDeckCard[];
  commanderCards?: EnrichedDeckCard[];
  companionCards?: EnrichedDeckCard[];
  isLocked?: boolean;
  deckFormat?: DeckFormat | null;
  /** Called when a card is selected to open details panel */
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
}

export default function DeckStackView({
  deckId,
  cards,
  commanderCards = [],
  companionCards = [],
  isLocked = false,
  onCardSelect,
}: DeckStackViewProps): ReactElement {
  const cardGroups = useCardGroups(cards);

  // Group cards based on selected grouping, then split large groups
  const stackGroups = useMemo((): SplitStack[] => {
    return splitIntoStacks(cardGroups);
  }, [cardGroups]);

  return (
    <>
      {/* Commander and Companion Section - prominent display at top */}
      {(commanderCards.length > 0 || companionCards.length > 0) && (
        <CommanderSection
          deckId={deckId}
          commanders={commanderCards}
          companions={companionCards}
          isLocked={isLocked}
          variant="stack"
          onCardSelect={onCardSelect}
        />
      )}

      <div className="grid auto-rows-min gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(176px, 1fr))' }}>
        {stackGroups.map((group) => (
          <CardStack
            key={group.key}
            group={group}
            stackIndex={group.stackIndex}
            totalStacks={group.totalStacks}
            onCardSelect={onCardSelect}
          />
        ))}
      </div>
    </>
  );
}
